import { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Header } from '../components/layout/Header.jsx'
import { Footer } from '../components/layout/Footer.jsx'
import { BillCard } from '../components/feed/BillCard.jsx'
import { CandidateCard } from '../components/feed/CandidateCard.jsx'
import { SignInModal } from '../components/auth/SignInModal.jsx'
import { useNostr } from '../hooks/useNostr.js'
import { useNostrLogin } from '../hooks/useNostrLogin.js'
import { useFeed } from '../hooks/useFeed.js'
import {
  loadJurisdiction,
  clearJurisdiction,
  jurisdictionTagValue,
} from '../utils/jurisdiction.js'
import {
  buildVoteEvent,
  buildQuoteEvent,
  rememberMyEvent,
  normalizeChoice,
} from '../utils/nostr.js'
import { track, trackFirstVote } from '../utils/analytics.js'
import { DISCLAIMER_TEXT } from '../utils/constants.js'
import {
  loadQuillState,
  recordVoteNow,
  checkMissedDay,
  emptyQuillState,
} from '../utils/quillStreak.js'

export default function PollPage() {
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // SEO-5: this page is mounted on both /poll/:id (hex) and /bill/:slug
  // (bill-id). When we land on a slug route, resolve to the hex id via
  // the pre-built polls-meta.json, then rewrite the URL to the
  // canonical /poll/<id> so share links and bookmarks consolidate.
  const [resolvedPollId, setResolvedPollId] = useState(params.id || '')
  useEffect(() => {
    if (params.id) {
      setResolvedPollId(params.id)
      return
    }
    if (!params.slug) return
    let cancelled = false
    fetch('/polls-meta.json', { cache: 'force-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((map) => {
        if (cancelled || !map) return
        const target = String(params.slug).toUpperCase()
        let best = null
        for (const [pid, meta] of Object.entries(map)) {
          if (!meta || typeof meta !== 'object') continue
          if ((meta.bill_id || '').toUpperCase() !== target) continue
          if (!best || (meta.created_at || 0) > (best.created_at || 0)) {
            best = { id: pid, created_at: meta.created_at || 0 }
          }
        }
        if (!cancelled && best) {
          setResolvedPollId(best.id)
          // Canonicalize the URL in the address bar so Google + sharers
          // see the hex form, not the slug. replace, not push — don't
          // bloat the back-button.
          navigate(`/poll/${best.id}${window.location.search}`, { replace: true })
        }
      })
      .catch(() => { /* noop */ })
    return () => { cancelled = true }
  }, [params.id, params.slug, navigate])
  const pollId = resolvedPollId

  // Share-link attribution. Fire once when the user lands with a ref.
  useEffect(() => {
    const ref = searchParams.get('ref')
    if (ref && pollId) track('referral', { ref, pollId })
    // Only fire on the first effect run for this pollId — re-renders
    // shouldn't re-fire even though searchParams is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId])

  const [jurisdiction, setJurisdiction] = useState(() => loadJurisdiction())
  const [authModal, setAuthModal] = useState(null)
  // UX30: forces the modal to open on the Create view when set, overriding
  // SignInModal's auto-select. Set by Header's "Create account" button.
  const [authModalTab, setAuthModalTab] = useState(null)
  // UX63: quill state so votes cast from a direct /poll link also tick
  // the streak + so the Header badge reflects reality on this page.
  const [quillState, setQuillState] = useState(() => emptyQuillState())

  const userJurisdictionTag = useMemo(
    () => jurisdictionTagValue(jurisdiction),
    [jurisdiction]
  )

  const nostr = useNostr()
  const auth = useNostrLogin()

  // UX63: hydrate quill state on pubkey change, matching Home.jsx.
  // Signed-out → empty; signed-in → load from LS + run the miss check
  // so the Header badge shows the right streak on this page too, and
  // so the toggle inside recordVoteNow has the correct state to read
  // on the next vote.
  useEffect(() => {
    if (!auth.pubkey) {
      setQuillState(emptyQuillState())
      return
    }
    checkMissedDay(auth.pubkey)
    setQuillState(loadQuillState(auth.pubkey))
  }, [auth.pubkey])
  // UX29 Phase 1.5: route locked Google users to the 'google-unlock'
  // confirm-and-OAuth view instead of the passphrase one. Reads `auth`
  // so declared after useNostrLogin() above.
  const unlockMode = () => auth.loginType === 'google' ? 'google-unlock' : 'unlock'

  // Logout also clears the saved jurisdiction so a shared device
  // doesn't inherit the previous user's location. Mirrors Home.jsx.
  const handleLogout = () => {
    auth.logout()
    clearJurisdiction()
    setJurisdiction(null)
  }
  const { items, tallies, loading, voteIndex, addLocalVote } = useFeed({
    pool: nostr.pool,
    subscribe: nostr.subscribe,
    userJurisdiction: userJurisdictionTag,
    userPubkey: auth.pubkey,
  })

  const item = items.find((i) => i.id === pollId) || null

  const itemWithResidency = useMemo(() => {
    if (!item) return null
    return {
      ...item,
      resident: userJurisdictionTag
        ? userJurisdictionTag.startsWith(item.jurisdiction) ||
          item.jurisdiction.startsWith(userJurisdictionTag)
        : null,
    }
  }, [item, userJurisdictionTag])

  const userVote = auth.pubkey && voteIndex[pollId]?.[auth.pubkey]
    ? normalizeChoice(voteIndex[pollId][auth.pubkey].choice)
    : null

  // Set page title + OG meta tags client-side. Real users on a deep link
  // see the right tab title and shareable URL; social-card crawlers fall
  // back to homepage defaults until the Edge Function ships (UX01b).
  useEffect(() => {
    if (!item) return
    const origTitle = document.title
    const title = `${item.title || item.billId || 'Poll'} — Verdant`
    const desc = (item.plainSummary || '').slice(0, 200)
      || 'Vote on this proposal at Verdant.'
    const url = window.location.href

    document.title = title
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('property', 'og:url', url)
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', desc)
    upsertMeta('name', 'description', desc)

    return () => { document.title = origTitle }
  }, [item])

  const handleShareQuote = async ({ item: it, title, shareUrl, comment }) => {
    if (!auth.isLoggedIn) {
      setAuthModal(auth.locked ? unlockMode() : 'choose')
      return false
    }
    try {
      const template = buildQuoteEvent({
        pollEventId: it.id,
        pollAuthor: it.pubkey,
        title,
        shareUrl,
        comment,
      })
      const signed = await auth.sign(template)
      const result = await nostr.publish(signed)
      console.info('[Verdant] published quote', {
        id: signed.id, pollId: it.id,
        accepted: result.accepted, total: result.total,
      })
      rememberMyEvent(signed)
      return true
    } catch (err) {
      console.error('[Verdant] quote failed', err)
      alert(err?.message || 'Failed to publish quote.')
      return false
    }
  }

  const handleVote = async (it, choice) => {
    // UX41: match Home.jsx — locked users unlock; everyone else gets
    // an ephemeral key auto-generated so the vote lands without an
    // auth-modal detour.
    if (auth.locked) {
      setAuthModal(unlockMode())
      return
    }
    if (!auth.isLoggedIn) {
      const pk = auth.beginEphemeral()
      if (!pk) return
    }
    const existing = voteIndex[it.id]?.[auth.pubkey]
    if (existing?.choice?.toLowerCase() === choice.toLowerCase()) return
    try {
      const template = buildVoteEvent({
        pollEventId: it.id,
        pollAuthor: it.pubkey,
        choice,
        userJurisdiction: userJurisdictionTag,
        poll: it,
      })
      const signed = await auth.sign(template)
      const result = await nostr.publish(signed)
      try { navigator?.vibrate?.(20) } catch { /* noop */ }
      addLocalVote(signed)
      rememberMyEvent(signed)
      trackFirstVote({ pubkey: auth.pubkey, level: it.level })
      // UX63: credit the vote to the quill streak. Without this, votes
      // from a direct /poll/<id> page (share links, bookmarks, SEO
      // landing pages) were silently skipped — the counter only ran
      // on Home.jsx — which is why the streak appeared stuck at 1 for
      // users whose main interaction surface was shared poll links.
      const quill = recordVoteNow(auth.pubkey)
      setQuillState(quill.state)
      console.info('[Verdant] published vote', {
        id: signed.id, choice, pollId: it.id,
        accepted: result.accepted, total: result.total,
      })
    } catch (err) {
      console.error('[Verdant] vote failed', err)
      alert(err?.message || 'Failed to publish vote.')
    }
  }

  return (
    <div className="app-shell">
      <Header
        jurisdiction={jurisdiction}
        isLoggedIn={auth.isLoggedIn}
        locked={auth.locked}
        npub={auth.npub}
        loginType={auth.loginType}
        quillState={quillState}
        onLogin={() => { setAuthModalTab(null); setAuthModal('choose') }}
        onCreateAccount={() => { setAuthModalTab('create'); setAuthModal('choose') }}
        onLogout={handleLogout}
        onUnlock={() => setAuthModal(unlockMode())}
        onJurisdictionClick={() => navigate('/')}
      />

      <main className="app-main">
        <div className="feed-disclaimer" role="note">
          {DISCLAIMER_TEXT}
        </div>

        <div style={{ marginBottom: 12 }}>
          <Link to="/" style={{ color: 'var(--forest)' }}>← Back to feed</Link>
        </div>

        {!item && loading && (
          <div className="loading-state">Loading poll from relays…</div>
        )}

        {!item && !loading && (
          <div className="empty-state">
            Poll not found on the relays we connect to.
          </div>
        )}

        {itemWithResidency && (
          itemWithResidency.kind === 'candidate' ? (
            <CandidateCard
              item={itemWithResidency}
              tally={tallies[itemWithResidency.id]}
              userVote={userVote}
              onVote={handleVote}
              onShareQuote={handleShareQuote}
              isLoggedIn={auth.isLoggedIn}
            />
          ) : (
            <BillCard
              item={itemWithResidency}
              tally={tallies[itemWithResidency.id]}
              userVote={userVote}
              onVote={handleVote}
              onShareQuote={handleShareQuote}
              isLoggedIn={auth.isLoggedIn}
            />
          )
        )}
      </main>

      <Footer />

      <SignInModal
        open={authModal !== null}
        mode={authModal || 'choose'}
        defaultTab={authModalTab}
        nostrAvailable={auth.nostrAvailable}
        error={auth.error}
        clearError={auth.clearError}
        onClose={() => { setAuthModal(null); setAuthModalTab(null) }}
        onLoginNip07={auth.loginNip07}
        onLoginWithGoogle={auth.loginWithGoogle}
        onImport={auth.importKey}
        onCreate={auth.createNew}
        onUnlock={auth.unlock}
        onLogout={handleLogout}
      />
    </div>
  )
}

function upsertMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', value)
}
